// Eskom PARCC — effective compliance for the Transactions register (read-only).
//
// The register shows each transaction's *effective* compliance level. The
// authoritative maths lives in the domain layer (`calculateTransactionCompliance`,
// src/domain/compliance.ts), which the monolith called as `calculateTransactionCompliance(uid)`
// off the per-transaction `raw{}` sheet-header map plus its finding lines.
//
// The React read layer (src/lib/db.ts) deliberately drops that `raw{}` map and
// surfaces typed fields instead, so we adapt: reconstruct only the handful of
// `raw` keys the domain helper actually reads — gaps status, the strict-rule /
// compliance-level override, the reviewed flag and the findings text — from the
// typed `Transaction`, then delegate. No compliance logic is reimplemented here;
// this is purely a typed → `raw` shim so the same domain function decides.
import { calculateTransactionCompliance, type TransactionLike } from '../../domain/compliance';
import type { Transaction, Finding } from '../../data/types';

/** Finding subset the domain compliance roll-up consumes (final/suggested level). */
export type FindingForCompliance = Pick<Finding, 'finalCompliance' | 'suggestedCompliance'>;

/** Finding subset needed to GROUP lines by transaction and roll their level up. */
export type FindingForComplianceByRef = FindingForCompliance &
  Pick<Finding, 'referenceNo' | 'deleted'>;

/**
 * Build the minimal `raw{}` map `calculateTransactionCompliance` reads, from the
 * typed transaction. Only the keys the domain helper looks up are populated:
 *  - gaps status   → `'Gaps Status'`
 *  - level override → `'Compliance level based on Updated Findings (Strict Rule)'`
 *  - reviewed flag  → `'Reviewed Y/N'` ('Yes'/'No')
 *  - findings text  → `'Updated findings'` (falls back to initial findings)
 * Blank fields are left out so `firstNonBlank(...)` in the domain layer behaves
 * exactly as it would on a sparse sheet row.
 */
function toTransactionLike(tx: Transaction): TransactionLike {
  const raw: Record<string, unknown> = {};
  if (tx.gapsStatus) raw['Gaps Status'] = tx.gapsStatus;
  if (tx.complianceLevelOverride) {
    raw['Compliance level based on Updated Findings (Strict Rule)'] = tx.complianceLevelOverride;
  } else if (tx.complianceLevel) {
    // The domain helper treats `Compliance Level` as a secondary override source.
    raw['Compliance Level'] = tx.complianceLevel;
  }
  if (tx.reviewed === true) raw['Reviewed Y/N'] = 'Yes';
  else if (tx.reviewed === false) raw['Reviewed Y/N'] = 'No';
  const findingsText = tx.updatedFindings || tx.findingText || tx.initialFindings;
  if (findingsText) raw['Updated findings'] = findingsText;

  return {
    mechanism: tx.mechanism,
    value: tx.value,
    raw,
    selfDeclaration: tx.selfDeclaration,
  };
}

/**
 * The effective compliance level shown for a transaction in the register — the
 * domain `calculateTransactionCompliance` result, layering any override on top of
 * the finding-line roll-up. `findings` should be the transaction's own finding
 * lines (those whose `referenceNo` matches); pass `[]` when none are loaded.
 */
export function effectiveCompliance(
  tx: Transaction,
  findings: readonly FindingForCompliance[] = [],
): string {
  return calculateTransactionCompliance(toTransactionLike(tx), [...findings]);
}

/**
 * Build a `referenceNo → effective compliance level` map for a set of transactions,
 * grouping every non-deleted finding line by its transaction first (exactly as the
 * register does, `TransactionsPage.tsx`). This is the ONE place the dashboard and the
 * reports should read compliance from, so their Compliant / Non-Compliant / Possible-IE
 * counts agree with the Transactions register — which rolls findings + overrides up even
 * when the stored `complianceLevel` is blank.
 *
 * Pass the FULL non-deleted finding set (not the register-filtered "reportable" subset):
 * a transaction whose lines are all Compliant must resolve to Compliant, which requires
 * seeing those lines. Soft-deleted transactions are skipped.
 */
export function effectiveComplianceByRef(
  transactions: readonly Transaction[],
  findings: readonly FindingForComplianceByRef[],
): Map<string, string> {
  const byRef = new Map<string, FindingForCompliance[]>();
  for (const f of findings) {
    if (f.deleted || !f.referenceNo) continue;
    const bucket = byRef.get(f.referenceNo);
    if (bucket) bucket.push(f);
    else byRef.set(f.referenceNo, [f]);
  }
  const out = new Map<string, string>();
  for (const tx of transactions) {
    if (tx.deleted) continue;
    out.set(tx.referenceNo, effectiveCompliance(tx, byRef.get(tx.referenceNo) ?? []));
  }
  return out;
}
